import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import PatientLayout from "@/components/layout/PatientLayout";
import api from "@/api";
import { toast } from "sonner";
import {
  BookOpen, Play, FileText, ChevronRight, Star, Clock,
  Heart, Utensils, AlertTriangle, Smile, Sparkles, CheckCircle,
  Stethoscope, Activity
} from "lucide-react";

// Category icons and colors
const CATEGORY_CONFIG = {
  BIENVENUE: { icon: Sparkles, color: "emerald", label: "Bienvenue" },
  TRAITEMENT: { icon: Stethoscope, color: "blue", label: "Traitement" },
  NUTRITION: { icon: Utensils, color: "orange", label: "Nutrition" },
  EFFETS_SECONDAIRES: { icon: Activity, color: "purple", label: "Effets secondaires" },
  SIGNES_ALERTE: { icon: AlertTriangle, color: "red", label: "Signes d'alerte" },
  BIEN_VIVRE: { icon: Smile, color: "teal", label: "Bien-être" },
  GESTION_SYMPTOMES: { icon: Heart, color: "pink", label: "Gestion des symptômes" },
};

const getCategoryConfig = (category) => {
  return CATEGORY_CONFIG[category] || { icon: BookOpen, color: "slate", label: category };
};

// Content card component
function ContentCard({ content, onClick }) {
  const { t } = useLanguage();
  const config = getCategoryConfig(content.category);
  const Icon = config.icon;
  
  // Translate category label
  const categoryLabel = t(`education.categories.${content.category}`);
  
  const isVideo = content.contentType === "VIDEO";
  const duration = content.durationSeconds 
    ? `${Math.round(content.durationSeconds / 60)} min`
    : null;

  // Translate "Vu" label
  const viewedLabel = t("education.viewed");

  const colorClasses = {
    emerald: "bg-emerald-100 text-emerald-600 border-emerald-200",
    blue: "bg-blue-100 text-blue-600 border-blue-200",
    orange: "bg-orange-100 text-orange-600 border-orange-200",
    purple: "bg-purple-100 text-purple-600 border-purple-200",
    red: "bg-red-100 text-red-600 border-red-200",
    teal: "bg-teal-100 text-teal-600 border-teal-200",
    pink: "bg-pink-100 text-pink-600 border-pink-200",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };

  return (
    <button
      onClick={onClick}
      data-testid={`education-content-${content.id}`}
      className="w-full text-left bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-emerald-200 transition-all active:scale-[0.98]"
    >
      {/* Thumbnail or placeholder */}
      {isVideo && content.thumbnailUrl ? (
        <div className="relative aspect-video bg-slate-100">
          <img
            src={content.thumbnailUrl}
            alt={content.title}
            className="w-full h-full object-cover"
            onError={(e) => e.target.style.display = 'none'}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
              <Play className="w-7 h-7 text-emerald-600 ml-1" fill="currentColor" />
            </div>
          </div>
          {duration && (
            <span className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded-lg flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {duration}
            </span>
          )}
        </div>
      ) : (
        <div className={`aspect-video flex items-center justify-center ${colorClasses[config.color].split(' ')[0]}`}>
          <Icon className={`w-16 h-16 ${colorClasses[config.color].split(' ')[1]} opacity-50`} />
        </div>
      )}

      {/* Content info */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorClasses[config.color]}`}>
            {categoryLabel}
          </span>
          {content.viewed && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-600 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {viewedLabel}
            </span>
          )}
        </div>
        <h3 className="font-semibold text-slate-800 line-clamp-2">{content.title}</h3>
        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{content.shortDescription}</p>
      </div>
    </button>
  );
}

// Section with horizontal scroll
function ContentSection({ title, contents, onContentClick, emptyMessage }) {
  const { t } = useLanguage();
  if (!contents || contents.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">{title}</h2>
        {contents.length > 2 && (
          <span className="text-sm text-emerald-600 font-medium">
            {contents.length} {t("education.contents")}
          </span>
        )}
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {contents.map((content) => (
          <div key={content.id} className="flex-shrink-0 w-64">
            <ContentCard content={content} onClick={() => onContentClick(content)} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PatientEducation() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [recommended, setRecommended] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [myContent, setMyContent] = useState([]);

  useEffect(() => {
    fetchEducationData();
  }, []);

  const fetchEducationData = async () => {
    try {
      const [recRes, catRes, statsRes, myRes] = await Promise.all([
        api.get("/education/patient/recommended?limit=10"),
        api.get("/education/patient/categories"),
        api.get("/education/patient/stats"),
        api.get("/education/patient/my-content?limit=10"),
      ]);

      setRecommended(recRes.data);
      setCategories(catRes.data);
      setStats(statsRes.data);
      setMyContent(myRes.data);
    } catch (err) {
      // error handled by toast
      toast.error(t("education.loadContentsError"));
    } finally {
      setLoading(false);
    }
  };

  const handleContentClick = (content) => {
    navigate(`/patient/education/${content.id}`);
  };

  if (loading) {
    return (
      <PatientLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </PatientLayout>
    );
  }

  // Group content by category for display
  const contentByCategory = {};
  recommended.forEach(content => {
    if (!contentByCategory[content.category]) {
      contentByCategory[content.category] = [];
    }
    contentByCategory[content.category].push(content);
  });

  return (
    <PatientLayout>
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="animate-fade-in-up">
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <BookOpen className="w-7 h-7 text-emerald-600" />
            {t("education.title")}
          </h1>
          <p className="text-slate-500 mt-1" data-testid="patient-education-subtitle">
            {t("education.subtitle")}
          </p>
        </div>

        {/* Stats card */}
        {stats && stats.totalAssigned > 0 && (
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-4 border border-emerald-100 animate-fade-in-up stagger-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">{t("education.myProgress")}</p>
                <p className="text-2xl font-bold text-emerald-700">
                  {t("education.consultedProgress")
                    .replace("{completed}", stats.completed)
                    .replace("{total}", stats.totalAssigned)}
                </p>
              </div>
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                <span className="text-xl font-bold text-emerald-600">
                  {stats.completionRate}%
                </span>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-2 bg-white rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                style={{ width: `${stats.completionRate}%` }}
              />
            </div>
          </div>
        )}

        {/* Recommended for you section */}
        {recommended.length > 0 && (
          <div className="space-y-3 animate-fade-in-up stagger-2">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" fill="currentColor" />
              <h2 className="text-lg font-bold text-slate-800">{t("education.recommended")}</h2>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {recommended.slice(0, 3).map((content) => (
                <ContentCard 
                  key={content.id} 
                  content={content} 
                  onClick={() => handleContentClick(content)} 
                />
              ))}
            </div>
          </div>
        )}

        {/* Categories section */}
        <div className="space-y-3 animate-fade-in-up stagger-3">
          <h2 className="text-lg font-bold text-slate-800">{t("education.explore")}</h2>
          <div className="grid grid-cols-2 gap-3">
            {categories.map((cat) => {
              const config = getCategoryConfig(cat.category);
              const Icon = config.icon;
              const colorClasses = {
                emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
                blue: "bg-blue-50 border-blue-200 text-blue-700",
                orange: "bg-orange-50 border-orange-200 text-orange-700",
                purple: "bg-purple-50 border-purple-200 text-purple-700",
                red: "bg-red-50 border-red-200 text-red-700",
                teal: "bg-teal-50 border-teal-200 text-teal-700",
                pink: "bg-pink-50 border-pink-200 text-pink-700",
                slate: "bg-slate-50 border-slate-200 text-slate-700",
              };
              
              return (
                <Link
                  key={cat.category}
                  to={`/patient/education/category/${cat.category}`}
                  data-testid={`education-category-${cat.category}`}
                  className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 hover:shadow-md transition-all ${colorClasses[config.color]}`}
                >
                  <Icon className="w-8 h-8" />
                  <span className="font-semibold text-center text-sm">{t(`education.categories.${cat.category}`)}</span>
                  <span className="text-xs opacity-70">{cat.count} {t("education.contents")}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* My recent content */}
        {myContent.length > 0 && (
          <div className="space-y-3 animate-fade-in-up stagger-4">
            <h2 className="text-lg font-bold text-slate-800">{t("education.myRecent")}</h2>
            <div className="space-y-3">
              {myContent.slice(0, 5).map((assignment) => {
                const content = assignment.content;
                const config = getCategoryConfig(content.category);
                const Icon = config.icon;
                
                return (
                  <button
                    key={assignment.id}
                    onClick={() => handleContentClick(content)}
                    className="w-full flex items-center gap-4 p-3 bg-white rounded-xl border border-slate-200 hover:border-emerald-200 hover:shadow-md transition-all"
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      assignment.status === "COMPLETED" ? "bg-green-100" : "bg-slate-100"
                    }`}>
                      {assignment.status === "COMPLETED" ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <Icon className="w-6 h-6 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-slate-800 line-clamp-1">{content.title}</p>
                      <p className="text-xs text-slate-500">
                        {assignment.status === "COMPLETED" ? t("education.completed") :
                         assignment.status === "OPENED" ? t("education.inProgress") :
                         t("education.toWatch")}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {recommended.length === 0 && myContent.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">
              {t("education.noContent")}
            </h3>
            {t("education.contentWillAppear") && (
              <p className="text-slate-500">
                {t("education.contentWillAppear")}
              </p>
            )}
          </div>
        )}
      </div>
    </PatientLayout>
  );
}
